import type { DmaMatrixTopic, DmaMatrixValence } from '@upright/common/mcp-payloads/dma-matrix';
import { SustainabilityMatterId } from '@upright/common/model/input/materiality-assessment/types-matters';
import {
  getMatrixTopicOf,
  getPlacementValence,
} from '@upright/common/model/output/materiality/materiality-matrix/topics';
import {
  MaterialityMatrixLevel,
  MatrixMattersByBucket,
} from '@upright/common/model/output/materiality/materiality-matrix/types';
import {
  compareMatrixMattersInCell,
  getMatterBucketId,
} from '@upright/common/model/output/materiality/materiality-matrix/utils';

export const IMPACT_LEVELS_TOP_DOWN: MaterialityMatrixLevel[] = ['high', 'medium', 'low'];
export const FINANCIAL_LEVELS_LEFT_RIGHT: MaterialityMatrixLevel[] = ['low', 'medium', 'high'];

export interface MatrixPlacement {
  matterId: SustainabilityMatterId;
  valence: DmaMatrixValence;
}

function isShown(matterId: SustainabilityMatterId, selectedTopicIds: readonly string[]): boolean {
  if (!selectedTopicIds.length) return true;
  const topicId = getMatrixTopicOf(matterId);
  return topicId !== null && selectedTopicIds.includes(topicId);
}

/** One named cell of the matter matrix, in the order the chat card lists it (highest impact score first). */
export function getCellPlacements(
  matrixData: MatrixMattersByBucket,
  impactLevel: MaterialityMatrixLevel,
  financialLevel: MaterialityMatrixLevel,
  selectedTopicIds: readonly string[],
): MatrixPlacement[] {
  return [...matrixData[getMatterBucketId(impactLevel, financialLevel)]]
    .sort(compareMatrixMattersInCell)
    .filter(m => isShown(m.matterId, selectedTopicIds))
    .map(m => ({ matterId: m.matterId, valence: getPlacementValence(m) }));
}

/** The low × low cell is a count, as on the chat card: low or no materiality on both axes. */
export function countLowOrNoMateriality(
  matrixData: MatrixMattersByBucket,
  selectedTopicIds: readonly string[],
): number {
  return matrixData[getMatterBucketId('low', 'low')].filter(m => isShown(m.matterId, selectedTopicIds)).length;
}

/** The topics the filter can narrow to: those with a material matter or a matter placed in a named cell. */
export function getSelectableTopicIds(topics: DmaMatrixTopic[], matrixData: MatrixMattersByBucket): Set<string> {
  const ids = new Set(topics.filter(t => t.materialMatters.length > 0).map(t => t.topicId));
  const lowLowBucket = getMatterBucketId('low', 'low');
  for (const [bucketId, matters] of Object.entries(matrixData)) {
    if (bucketId === lowLowBucket) continue;
    for (const matter of matters) {
      const topicId = getMatrixTopicOf(matter.matterId);
      if (topicId) ids.add(topicId);
    }
  }
  return ids;
}
